import React, { useEffect, useState, useRef } from 'react';
import {
	TextField,
	InputLabel,
	MenuItem,
	FormControl,
	Select,
	FormHelperText,
	CircularProgress,
	Avatar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { getRoomsEnrollStd } from '@/app/services/rooms/rooms';
import { enrollStudent, getExistingParents, preFetchData } from '@/app/services/students/students';
import { uploadFile } from '@/app/services/imageUpload/imageUpload';
import './enrollStudent.css';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from '@/app/store/actions';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import CustomDatePicker from '@shared/customComponents/CustomDatePicker/CustomDatePicker';
import { inviteSentence } from './enrollmentInvite';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { scrollIntoView, getImageUrl, isImageFile, isInValidName } from '@/utils/utils';
import { getSearchableCityList, getSearchableStateList } from '@/app/services/schools/schools';
import { getCountryList } from '@/app/services/settings/settings';
import { debounce } from 'lodash';
import { isPossiblePhoneNumber } from 'libphonenumber-js/min';
import CustomPhoneInput from '@/app/customComponents/CustomPhoneInput/CustomPhoneInput';
import PageHeader from '@/app/customComponents/PageHeader';

const useStyles = makeStyles({
	select: {
		'&:before': {
			borderBottom: 'none',
		},
		'&:after': {
			borderBottom: 'none',
		},
		'&:not(.Mui-disabled):hover::before': {
			borderBottom: 'none',
		},
		'& .MuiSelect-select:focus': {
			backgroundColor: 'inherit',
		},
		'& .MuiSvgIcon-root': {
			color: 'inherit',
		},
		color: 'inherit',
		'&:hover': {
			color: 'inherit',
		},
	},
	content: {
		position: 'relative',
		display: 'flex',
		overflow: 'auto',
		flex: '1 1 auto',
		flexDirection: 'column',
		width: '100%',
		'-webkit-overflow-scrolling': 'touch',
		zIndex: 2,
	},
});
/** ScheduledStudentStartService::MAX_YEARS_AHEAD — keep in step with the API. */
const MAX_YEARS_AHEAD = 2;

const EnrollStudents = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	// No tuition plan to offer at a school with billing switched off, so the
	// future-start helper line does not mention one.
	const billingEnabled = useSelector(({ auth }) => (auth?.user?.school ?? auth?.user?.data?.school)?.billing_enabled);
	const navigate = useNavigate();
	const [form, setForm] = useState({
		city: '',
		zip_code: '',
		state_id: '',
		parent_first_name: '',
		parent_last_name: '',
		parent_phone: '',
		gender: '',
		room_id: '',
		country_id: '',
		isNew: 0,
		is_primary_account: 1,
		// Defaults to today, which the API treats exactly as no date at all:
		// enrol now, invite now. Only a later date schedules anything.
		enrollment_start_date: dayjs().format('YYYY-MM-DD'),
	});
	const [rooms, setRooms] = useState([]);
	const [page, setPage] = useState(1);
	const [errTxts, setErrTxts] = useState({
		city: '',
		zipcode: '',
		state_id: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [date, setDate] = useState(null);
	const [startDate, setStartDate] = useState(dayjs());
	const [existingParents, setExistingParents] = useState([]);
	const [parentEmail, setParentEmail] = useState('');
	const [role, setRole] = useState('parent');
	const [loadingParents, setLoadingParents] = useState(false);
	const [preview, setPreview] = useState();
	const [selectedFile, setSelectedFile] = useState(null);
	const [searchStateQuery, setStateSearchQuery] = useState('');
	const [isCityLoading, setIsCityLoading] = useState(false);
	const [searchCityQuery, setSearchCityQuery] = useState('');
	const [defaultState, setDefaultState] = useState('');
	const [countries, setCountries] = useState([]);
	const [city, setCity] = useState([]);
	const [states, setStates] = useState([]);
	const [isStateloading, setIsStateloading] = useState(false);

	const inputRef = useRef(null);

	// Compared as calendar days in the browser's zone. The API decides this
	// again in the SCHOOL's timezone, which is the authority — this only
	// drives the helper text and the success toast.
	const startsInFuture = startDate ? dayjs(startDate).startOf('day').isAfter(dayjs().startOf('day')) : false;

	useEffect(() => {
		let isMounted = true;
		getCountryList()
			.then((res) => {
				if (!isMounted) return;
				setCountries(res.data);
			})
			.catch(() => {
				if (!isMounted) return;
				dispatch(
					Actions.showMessage({
						message: 'Failed to get countries.',
						autoHideDuration: 1500,
						variant: 'error',
					})
				);
			});
		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		let isMounted = true;
		// if email doesnt exist or invalid email is entered, dont call api
		if (!form?.parent_email) return;
		if (!/^\S+@\S+\.\S+$/.test(form.parent_email)) return;

		const fetchData = async () => {
			// Make your API call here
			const res = await preFetchData(form?.parent_email?.trim());

			if (res?.data) {
				if (!isMounted) return;
				setForm((prevForm) => ({
					...prevForm,
					parent_first_name: res?.data?.first_name,
					parent_last_name: res?.data?.last_name,
					parent_phone: res?.data?.phone,
				}));
			}
		};

		// Create a debounced function with a delay of 1000ms
		const debouncedFetchData = debounce(fetchData, 1000);

		// Call the debounced function every time form.email changes
		if (form.isNew == 1) {
			debouncedFetchData();
		}

		// Cleanup function to cancel the debounced function
		return () => {
			isMounted = false;
			debouncedFetchData.cancel();
		};
	}, [form?.parent_email]);

	useEffect(() => {
		let isMounted = true;
		const timeout = setTimeout(() => {
			if (!isMounted) return;
			setIsStateloading(true);

			getSearchableStateList(searchStateQuery, searchStateQuery ? undefined : 1)
				.then((res) => {
					if (!isMounted) return;
					setStates(res?.data?.data);
				})
				.catch(() => {
					if (!isMounted) return;
					dispatch(
						Actions.showMessage({
							message: 'Failed to get states.',
							autoHideDuration: 1500,
							variant: 'error',
						})
					);
				})
				.finally(() => {
					if (!isMounted) return;
					setIsStateloading(false);
				});
		}, 1000);
		return () => {
			isMounted = false;
			clearTimeout(timeout);
		};
	}, [dispatch, searchStateQuery]);

	useEffect(() => {
		let isMounted = true;
		const timeout = setTimeout(() => {
			if (!isMounted) return;
			setIsCityLoading(true);
			setForm({ ...form, city: '' });
			setCity([]);
			if (!searchCityQuery && !form.state_id) {
				setForm({ ...form, city: '' });
				setCity([]);
			} else {
				getSearchableCityList(form?.state_id, searchCityQuery, 1)
					.then((res) => {
						if (!isMounted) return;
						setForm({ ...form, city: '' });
						setCity(res?.data?.data);
					})
					.catch(() => {
						if (!isMounted) return;
						dispatch(
							Actions.showMessage({
								message: 'Failed to get cities.',
								autoHideDuration: 1500,
								variant: 'error',
							})
						);
					})
					.finally(() => {
						if (!isMounted) return;
						setIsCityLoading(false);
					});
			}
		}, 1000);
		return () => {
			isMounted = false;
			clearTimeout(timeout);
		};
	}, [searchCityQuery, defaultState]);

	useEffect(() => {
		let isMounted = true;
		const timeout = setTimeout(() => {
			if (!isMounted) return;
			setLoadingParents(true);
			getExistingParents(parentEmail)
				.then((res) => {
					if (!isMounted) return;
					setExistingParents(res?.data?.data);
					setLoadingParents(false);
				})
				.catch(() => {
					if (!isMounted) return;
					setLoadingParents(false);
				});
		}, 1000);
		return () => {
			isMounted = false;
			clearTimeout(timeout);
		};
	}, [parentEmail]);

	useEffect(() => {
		if (!selectedFile) {
			setPreview(undefined);
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFile);
		setPreview(objectUrl);

		return () => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	const onSelectFile = (e) => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(undefined);
			return;
		}
		const file = e.target.files[0];
		if (!isImageFile(file)) {
			dispatch(Actions.showMessage({ message: 'Please select an image file', variant: 'error' }));
		} else {
			setSelectedFile(e.target.files[0]);
		}
	};

	const handleChange = (ev) => {
		const { name, value } = ev.target;
		if (name === 'state_id') {
			setErrTxts({ ...errTxts, state_id: '' });
			return;
		}
		if (name === 'city') {
			setErrTxts({ ...errTxts, city: '' });
		}
		if (name === 'zip_code') {
			// Only Number, Max 5, can be empty
			if (value && !/^[0-9]{0,5}$/.test(value)) {
				return;
			}
			setErrTxts({ ...errTxts, zipcode: '' });
		}
		if (value == 1) {
			setForm({
				...form,
				parent_first_name: '',
				parent_last_name: '',
				parent_phone: '',
				[name]: value,
			});
			setErrTxts({ ...errTxts, [name]: [] });
			return;
		}
		setErrTxts({ ...errTxts, [name]: [] });
		setForm({ ...form, [name]: value });
	};

	const handleSubmit = async (ev) => {
		ev.preventDefault();
		setErrTxts({});
		if (!form.first_name) {
			setErrTxts({ ...errTxts, first_name: 'This field is required' });
			scrollIntoView('fname-student');
			return;
		}
		if (form.first_name && isInValidName(form.first_name)) {
			setErrTxts({ ...errTxts, first_name: 'Please enter a valid name.' });
			scrollIntoView('fname-student');
			return;
		}
		form.first_name = form.first_name.trim();
		if (!form.last_name) {
			setErrTxts({ ...errTxts, last_name: 'This field is required' });
			scrollIntoView('lname-student');
			return;
		}
		if (form.last_name && isInValidName(form.last_name)) {
			setErrTxts({ ...errTxts, last_name: 'Please enter a valid name.' });
			scrollIntoView('lname-student');
			return;
		}
		form.last_name = form.last_name.trim();
		if (!form.dob) {
			setErrTxts({ ...errTxts, dob: 'This field is required' });
			scrollIntoView('date-of-birth');
			return;
		}
		if (!form.room_id) {
			setErrTxts({ ...errTxts, room_id: 'This field is required' });
			scrollIntoView('homeroom');
			return;
		}
		if (!form.gender) {
			setErrTxts({ ...errTxts, gender: 'This field is required' });
			scrollIntoView('gender');
			return;
		}

		if (!form.isNew) {
			setErrTxts({ ...errTxts, isNew: 'This field is required' });
			scrollIntoView('isNew');
			return;
		}
		if (form.isNew === 1) {
			if (!form.parent_first_name) {
				setErrTxts({ ...errTxts, parent_first_name: 'This field is required' });
				scrollIntoView('parent_first_name');
				return;
			}
			if (form.parent_first_name && isInValidName(form.parent_first_name)) {
				setErrTxts({ ...errTxts, parent_first_name: 'Please enter a valid name.' });
				scrollIntoView('parent_first_name');
				return;
			}
			form.parent_first_name = form.parent_first_name.trim();
			if (!form.parent_last_name) {
				setErrTxts({ ...errTxts, parent_last_name: 'This field is required' });
				scrollIntoView('parent_last_name');
				return;
			}
			if (form.parent_last_name && isInValidName(form.parent_last_name)) {
				setErrTxts({ ...errTxts, parent_last_name: 'Please enter a valid name.' });
				scrollIntoView('parent_last_name');
				return;
			}
			form.parent_last_name = form.parent_last_name.trim();
			if (!form.relation_with_child) {
				setErrTxts({ ...errTxts, relation_with_child: 'This field is required' });
				scrollIntoView('relation_with_child');
				return;
			}
			if (!form.parent_phone) {
				setErrTxts({ ...errTxts, parent_phone: 'This field is required' });
				scrollIntoView('parent_phone');
				return;
			}
			if (!form.parent_email) {
				setErrTxts({ ...errTxts, parent_email: 'This field is required' });
				scrollIntoView('parent_email');
				return;
			}
			if (!/^\S+@\S+\.\S+$/.test(form.parent_email)) {
				setErrTxts({ ...errTxts, parent_email: 'Please enter valid email' });
				scrollIntoView('parent_email');
				return;
			}
			if (form.parent_phone) {
				if (isPossiblePhoneNumber(form.parent_phone, 'US') === false) {
					setErrTxts({ ...errTxts, parent_phone: 'Please enter valid phone number' });
					scrollIntoView('parent_phone');
					return;
				}
			}
		}
		if (form.isNew === 2) {
			if (!form.parent_id) {
				setErrTxts({ ...errTxts, parent_id: 'This field is required' });
				scrollIntoView('parent_id');
				return;
			}
			delete form.parent_first_name;
			delete form.parent_last_name;
			delete form.parent_phone;
			delete form.parent_email;
			delete form.relation_with_child;
		}
		if (form.isNew === 1) {
			form.emergency_contact = true;
			delete form.parent_id;
		}
		if (form.isNew === 2) {
			form.emergency_contact = true;
		}
		if (!form.city_id) {
			delete form.city_id;
		}
		if (!form.state_id) {
			delete form.state_id;
		}
		if (!form.zip_code) {
			delete form.zip_code;
		}
		setIsLoading(true);
		if (selectedFile) {
			const filename = getImageUrl(selectedFile);
			try {
				const response = await uploadFile(selectedFile, filename);
				form.photo = `${import.meta.env.VITE_S3_BASE_URL}${response}`;
				form.thumb = `${import.meta.env.VITE_S3_BASE_URL}${response}`;
			} catch (err) {
				dispatch(
					Actions.showMessage({
						message: 'Failed to upload image.',
						autoHideDuration: 2000,
						variant: 'error',
					})
				);
				setIsLoading(false);
				return;
			}
		} else {
			form.photo = `${import.meta.env.VITE_S3_BASE_URL}${import.meta.env.VITE_PERSONAL_AVATAR_URL}`;
			form.thumb = `${import.meta.env.VITE_S3_BASE_URL}${import.meta.env.VITE_PERSONAL_AVATAR_URL}`;
		}
		enrollStudent(form)
			.then((res) => {
				// The API is the authority on whether this scheduled or
				// enrolled now — it decides in the school's timezone, which
				// can disagree with the browser's near midnight.
				const scheduled = res?.data?.enrollment_start ?? null;
				dispatch(
					Actions.showMessage({
						message: scheduled
							? `Student added. They become active on ${dayjs(scheduled.start_date).format('MMM D, YYYY')}.`
							: 'Student Enrolled Successfully.',
						autoHideDuration: scheduled ? 4000 : 1500,
						variant: 'success',
					})
				);
				// Land on the list filtered to pending starts, so the child
				// they just added is actually on screen — the default Active
				// view cannot show them.
				navigate(scheduled ? '/students?enrollment=pending' : '/students');
			})
			.catch((err) => {
				dispatch(
					Actions.showMessage({
						message: err?.response?.data?.message || 'Failed to enroll student.',
						autoHideDuration: 2000,
						variant: 'error',
					})
				);
				if (err?.response?.data?.errors) {
					setErrTxts(err?.response?.data?.errors);
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		let isMounted = true;
		getRoomsEnrollStd('', page)
			.then((res) => {
				if (!isMounted) return;
				setRooms([...rooms, ...res.data.data]);
				if (res?.data?.current_page < res?.data?.last_page) {
					setPage(page + 1);
				}
			})
			.catch(() => {
				if (!isMounted) return;
				dispatch(
					Actions.showMessage({
						message: 'Failed to get rooms.',
						autoHideDuration: 1500,
						variant: 'error',
					})
				);
			});
		return () => {
			isMounted = false;
		};
	}, [page, dispatch]);

	useEffect(() => {
		if (date) {
			if (dayjs(date).format('YYYY-MM-DD') === form.dob) {
				return;
			}
			form.dob = dayjs(date).format('YYYY-MM-DD');
		} else {
			form.dob = '';
		}
	}, [date, form.dob]);

	// Same shape as the dob sync above. Clearing the picker falls back to
	// today rather than to an empty value: "no enrollment date" and "starts
	// today" are the same thing to the API, and an empty string would just
	// be a date the backend has to defend against.
	useEffect(() => {
		const next = startDate ? dayjs(startDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
		if (next !== form.enrollment_start_date) {
			form.enrollment_start_date = next;
		}
	}, [startDate, form.enrollment_start_date]);

	const handleGoBack = () => {
		navigate(-1);
	};

	return (
		<div className="px-64 py-60 mb-64">
			<PageHeader onBack={handleGoBack} title="Enroll Student" />
			<div className="bg-white p-60 mt-20 rounded-12 shadow-lg">
				<form onSubmit={handleSubmit}>
					<div className="gap-36 justify-center items-center flex flex-col md:flex-row md:items-start">
						<div
							className="relative pic-upload-overlay cursor-pointer flex-grow"
							onClick={() => inputRef.current.click()}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									inputRef.current.click();
								}
							}}
							role="button"
							tabIndex={0}
						>
							<Avatar className="imageupload" src={preview} style={{ height: '140px', width: '140px' }} />
							<div className="pp-input-overlay studentAdd">
								<i className="fa fa-2x fa-camera" />
							</div>
							<input
								accept="image/*"
								className="hidden"
								id="image"
								name="image"
								onChange={onSelectFile}
								ref={inputRef}
								type="file"
							/>
						</div>
						<fieldset style={{ width: '100%', marginLeft: '20px' }}>
							<div className=" md:mb-16">
								<h2 className="form-section-heading" style={{ fontSize: '18px' }}>
									Student Information
								</h2>
							</div>
							<div className="grid md:grid-cols-2 grid-cols-1 gap-x-40 gap-y-20">
								<TextField
									error={!!errTxts.first_name?.length}
									fullWidth
									helperText={errTxts.first_name}
									id="fname-student"
									label="First Name*"
									name="first_name"
									onChange={handleChange}
								/>
								<TextField
									error={!!errTxts.last_name?.length}
									fullWidth
									helperText={errTxts.last_name}
									id="lname-student"
									label="Last Name*"
									name="last_name"
									onChange={handleChange}
								/>

								<CustomDatePicker
									className="text-"
									disableFuture
									errTxts={errTxts.dob}
									fullWidth
									id="date-of-birth"
									label="Date of Birth*"
									name="dob"
									setValue={(d) => {
										setDate(d);
										setErrTxts({ ...errTxts, dob: '' });
									}}
									value={date}
								/>
								<FormControl className="" error={!!errTxts.room_id?.length} variant="standard">
									<InputLabel id="homeroomLabel">Select a Home Room*</InputLabel>
									<Select
										id="homeroom"
										label="Select a Home Room*"
										labelId="homeroomLabel"
										name="room_id"
										onChange={handleChange}
										value={form.room_id}
									>
										{rooms.length ? (
											rooms.map((room) => {
												return (
													<MenuItem id={room.name} key={room.id} value={room.id}>
														{room.name}
													</MenuItem>
												);
											})
										) : (
											<MenuItem disabled>Loading...</MenuItem>
										)}
									</Select>
									{errTxts.room_id ? <FormHelperText>{errTxts.room_id}</FormHelperText> : null}
								</FormControl>

								<div>
									<CustomDatePicker
										disablePast
										errTxts={errTxts.enrollment_start_date}
										fullWidth
										id="enrollment-start-date"
										label="Enrollment Start Date"
										maxDate={dayjs().add(MAX_YEARS_AHEAD, 'year')}
										minDate={dayjs()}
										setValue={(d) => {
											setStartDate(d);
											setErrTxts({ ...errTxts, enrollment_start_date: '' });
										}}
										value={startDate}
									/>
									{startsInFuture ? (
										<p className="text-12 text-gray-600 mt-4">
											{`${form.first_name || 'This student'} will be added as inactive and becomes active automatically on ${dayjs(
												startDate
											).format('MMM D, YYYY')}.${
												billingEnabled ? ' You can set up their tuition plan now.' : ''
											} ${inviteSentence(startDate)}`}
										</p>
									) : null}
								</div>

								<FormControl error={!!errTxts.gender?.length} variant="standard">
									<InputLabel id="genderLabel">Gender*</InputLabel>
									<Select
										id="gender"
										label="Select Gender*"
										labelId="genderLabel"
										name="gender"
										onChange={handleChange}
										value={form.gender}
									>
										<MenuItem value="male">
											<span id="male"> Male</span>
										</MenuItem>
										<MenuItem value="female">
											<span id="female"> Female</span>
										</MenuItem>
									</Select>
									{errTxts.gender ? <FormHelperText>{errTxts.gender}</FormHelperText> : null}
								</FormControl>
								<TextField id="address1" label="Address" name="address1" onChange={handleChange} />

								<FormControl className="">
									<TextField
										error={!!errTxts.country_id?.length}
										helperText={errTxts.country_id}
										id="country_id"
										label="Country"
										name="country_id"
										onChange={handleChange}
										select
										value={form.country_id}
									>
										{countries?.length ? (
											countries.map((country) => {
												return (
													<MenuItem key={country.id} value={country.id}>
														{country.name}
													</MenuItem>
												);
											})
										) : (
											<MenuItem>Loading...</MenuItem>
										)}
									</TextField>
								</FormControl>

								<Autocomplete
									autoComplete={false}
									clearOnBlur={false}
									disableClearable
									getOptionLabel={(option) => option.name}
									id="state-autocomplete"
									loading={isStateloading}
									loadingText="...Loading"
									name="state_id"
									onChange={(_e, v) => {
										setDefaultState(v);
										setForm({ ...form, state_id: v?.id || '' });
										setErrTxts({ ...errTxts, state_id: '' });
									}}
									onInputChange={(e, value) => {
										setStateSearchQuery(value);
										if (value === '') {
											setForm({ ...form, state_id: '', city: '' });
										}
									}}
									options={states}
									renderInput={(params) => (
										<TextField
											{...params}
											autoComplete="off"
											error={!!errTxts.state_id?.length}
											helperText={errTxts.state_id}
											label="State"
										/>
									)}
									renderOption={(option) => (
										<div className="flex" style={{ gap: 10 }}>
											<div>{option.name}</div>
										</div>
									)}
								/>

								<Autocomplete
									autoComplete={false}
									className=""
									clearOnBlur={false}
									disableClearable
									getOptionLabel={(option) => option.name}
									id="city"
									loading={isCityLoading}
									loadingText="...Loading"
									name="city"
									onChange={(_e, v) => {
										setForm({ ...form, city_id: v?.id || '' });
										setErrTxts({ ...errTxts, city: '' });
									}}
									options={city}
									renderInput={(params) => (
										<TextField
											{...params}
											autoComplete="off"
											error={!!errTxts.city_id?.length}
											helperText={errTxts.city_id}
											label="City"
											onChange={(e) => {
												setSearchCityQuery(e.target.value);
												if (e.target.value === '') {
													setForm({ ...form, city_id: '' });
												}
											}}
											style={{ width: '100%' }}
										/>
									)}
									renderOption={(option) => (
										<div className="flex" style={{ gap: 10 }}>
											<div>{option.name}</div>
										</div>
									)}
									sx={{ width: '100%' }}
								/>
								<TextField
									error={!!errTxts.zipcode?.length}
									helperText={errTxts.zipcode}
									id="zip_code"
									label="Zip Code"
									name="zip_code"
									onChange={handleChange}
									value={form.zip_code}
								/>
							</div>
							<div className="mt-52 md:mb-16">
								<div className="flex justify-between">
									<h2 className="form-section-heading" style={{ fontSize: '18px' }}>
										Parent Information
									</h2>
									<FormControl className="" error={!!errTxts.isNew?.length} variant="standard">
										<div className="flex">
											<CustomButton fontSize="15px" height="40" variant="secondary" width="120px">
												<Select
													className={classes.select}
													defaultValue={0}
													id="isNew"
													name="isNew"
													onChange={handleChange}
													value={form.isNew}
												>
													<MenuItem className="hidden" disabled value={0}>
														Select
													</MenuItem>
													<MenuItem value={1}>
														<span id="new"> New </span>
													</MenuItem>
													<MenuItem value={2}>
														<span id="existing"> Existing </span>
													</MenuItem>
												</Select>
											</CustomButton>
										</div>
										{errTxts.isNew ? <FormHelperText>{errTxts.isNew}</FormHelperText> : null}
									</FormControl>
								</div>
							</div>
							{form.isNew === 1 ? (
								<fieldset className="grid md:grid-cols-2 grid-cols-1 gap-x-40 gap-y-20">
									<TextField
										error={!!errTxts.parent_first_name?.length}
										helperText={errTxts.parent_first_name}
										id="fname-parent"
										label="First Name*"
										name="parent_first_name"
										onChange={handleChange}
										value={form?.parent_first_name}
									/>
									<TextField
										error={!!errTxts.parent_last_name?.length}
										helperText={errTxts.parent_last_name}
										id="lname-parent"
										label="Last Name*"
										name="parent_last_name"
										onChange={handleChange}
										value={form?.parent_last_name}
									/>

									<FormControl variant="standard">
										<InputLabel id="selectLabel" shrink>
											Select*
										</InputLabel>
										<Select
											id="Select"
											label="Select*"
											labelId="selectLabel"
											onChange={(e) => setRole(e.target.value)}
											value={role}
										>
											<MenuItem value="parent">
												<span id="parent">Parent</span>
											</MenuItem>
											<MenuItem value="legal-guardian">
												<span id="guardian">Legal Guardian</span>
											</MenuItem>
										</Select>
									</FormControl>
									<FormControl error={errTxts.relation_with_child?.length} variant="standard">
										<InputLabel id="relationLabel">Relation with Child*</InputLabel>
										<Select
											id="childRelation"
											label="Relation with Child*"
											labelId="relationLabel"
											name="relation_with_child"
											onChange={handleChange}
										>
											{role === 'parent' ? (
												// Rendered as an array because select doesn't accept a fragment
												[
													<MenuItem key="father" value="father">
														<span id="father">Father</span>
													</MenuItem>,
													<MenuItem key="mother" value="mother">
														<span id="mother">Mother</span>
													</MenuItem>,
												]
											) : (
												<MenuItem value="Legal Guardian">
													<span id="legal-guardian">Legal Guardian</span>
												</MenuItem>
											)}
										</Select>
										{errTxts.relation_with_child ? (
											<FormHelperText>{errTxts.relation_with_child}</FormHelperText>
										) : null}
									</FormControl>

									<CustomPhoneInput
										error={!!errTxts.parent_phone?.length}
										helperText={errTxts.parent_phone}
										id="parent-phone"
										label="Contact Number"
										name="parent_phone"
										onChange={handleChange}
										required
										value={form.parent_phone}
									/>
									<TextField
										error={!!errTxts.parent_email?.length}
										helperText={errTxts.parent_email}
										id="email-parent"
										label="Email Address*"
										name="parent_email"
										onChange={handleChange}
									/>
								</fieldset>
							) : form.isNew === 2 ? (
								<fieldset>
									<div className="grid md:grid-cols-2 grid-cols-1 gap-x-40 gap-y-20">
										<FormControl variant="standard">
											<InputLabel id="selectLabel" shrink>
												Select*
											</InputLabel>
											<Select defaultValue="parent" id="Select" label="Select*" labelId="selectLabel">
												<MenuItem value="parent">
													<span id="parent">Parent</span>
												</MenuItem>
											</Select>
										</FormControl>
										<Autocomplete
											autoComplete={false}
											clearOnBlur={false}
											getOptionLabel={(option) => option.name}
											id="parent-autocpmplete"
											loading={loadingParents}
											loadingText="Loading..."
											onChange={(e, v) => setForm({ ...form, parent_id: v?.id })}
											options={existingParents}
											renderInput={(params) => (
												<TextField
													{...params}
													autoComplete="off"
													error={!!errTxts.parent_id?.length}
													helperText={errTxts.parent_id}
													id="parent-name"
													label="Contacts Name"
													onChange={(e) => setParentEmail(e.target.value)}
												/>
											)}
											renderOption={(option) => (
												<div className="flex" id={option.id} key={option.id} style={{ gap: 10 }}>
													<Avatar src={option.thumb || option.photo} />
													<div>
														<div>{option.name}</div>
														<div>{option.email}</div>
													</div>
												</div>
											)}
										/>
									</div>
								</fieldset>
							) : (
								''
							)}
						</fieldset>
					</div>
					<div className="flex justify-center" style={{ marginTop: '60px' }}>
						{!isLoading ? (
							<div className=" center-btn">
								<CustomButton
									onClick={() => {
										navigate(-1);
									}}
									variant="secondary"
									width="140px"
								>
									Cancel
								</CustomButton>

								<CustomButton fontSize="15px" id="submit" type="submit" variant="primary" width="140px">
									Submit
								</CustomButton>
							</div>
						) : (
							<div className="flex justify-center">
								<CircularProgress className="mx-auto" />
							</div>
						)}
					</div>
				</form>
			</div>
		</div>
	);
};

export default EnrollStudents;
